import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Loader2, GraduationCap, BarChart3, FileText, Globe, Award,
  Lock, CheckCircle2, Play, ChevronLeft, ChevronRight, Layers, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MediaEmbed from "@/components/MediaEmbed";
import ExercisePlayer from "@/components/exercises/ExercisePlayer";

const Stat = ({ value, label }) => (
  <div className="rounded-2xl bg-white/10 px-5 py-3 text-center min-w-[92px]">
    <div className="font-heading text-2xl font-extrabold text-secondary">{value}</div>
    <div className="text-[11px] text-white/70 mt-0.5">{label}</div>
  </div>
);

function LessonNode({ lesson, onOpen }) {
  const { t } = useI18n();
  const state = lesson.state || "available";
  const locked = !!lesson.locked;
  const styles = {
    completed: { ring: "bg-emerald-500 text-white ring-emerald-200", Icon: CheckCircle2 },
    available: { ring: "bg-primary text-primary-foreground ring-secondary/40", Icon: Play },
    locked: { ring: "bg-muted text-muted-foreground ring-border", Icon: Lock },
  };
  const s = styles[state] || styles.available;
  const NodeIcon = s.Icon;
  const exCount = (lesson.exercises || []).length;
  return (
    <button disabled={locked} data-testid={`student-lesson-node-${lesson.id}`}
      onClick={() => !locked && onOpen(lesson)}
      className={`w-full flex items-center gap-4 rounded-2xl border p-4 text-left transition-all ${
        locked ? "opacity-60 cursor-not-allowed border-border bg-muted/30"
               : "border-border bg-card hover:-translate-y-0.5 hover:shadow-md cursor-pointer"}`}>
      <span className={`h-12 w-12 rounded-2xl ring-2 flex items-center justify-center shrink-0 ${s.ring}`}>
        <NodeIcon className="h-6 w-6" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold truncate">{lesson.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {state === "completed" ? t("student.completed") : state === "available" ? t("student.available") : t("student.locked")}
          {exCount > 0 && !locked ? ` · ${exCount} ${t("student.exercises")}` : ""}
        </div>
      </div>
      {state === "completed" && typeof lesson.score === "number" &&
        <Badge variant="secondary" className="rounded-full font-bold">{lesson.score}/100</Badge>}
      {!locked && <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />}
    </button>
  );
}

export default function StudentDashboard() {
  const { t } = useI18n();
  const [courses, setCourses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [progress, setProgress] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openLesson, setOpenLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    try {
      const [c, o, p] = await Promise.all([
        api.get("/courses"), api.get("/student/overview"), api.get("/student/progress"),
      ]);
      setCourses(c.data); setOverview(o.data); setProgress(p.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCourse = async (id) => {
    try { const { data } = await api.get(`/courses/${id}`); setSelected(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const completeLesson = async (lessonId) => {
    setCompleting(true);
    try {
      await api.post(`/lessons/${lessonId}/complete`);
      toast.success(t("student.lesson_completed"));
      setOpenLesson(null);
      const [c, o] = await Promise.all([api.get(`/courses/${selected.id}`), api.get("/student/overview")]);
      setSelected(c.data); setOverview(o.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCompleting(false); }
  };

  const tabCls = "rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-5 py-2.5 font-semibold";

  // ---- Course learning-path view ----
  if (selected) {
    const legacy = selected.lessons || [];
    return (
      <AppLayout>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <Button variant="ghost" onClick={() => setSelected(null)} className="rounded-full mb-4" data-testid="back-to-courses">
            <ChevronLeft className="h-4 w-4 mr-1" />{t("student.back_to_courses")}
          </Button>
          <div className="flex items-center gap-3 mb-1">
            <span className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center ring-2 ring-secondary/40 shrink-0">
              <BookOpen className="h-6 w-6 text-secondary" />
            </span>
            <div>
              <h1 className="font-heading text-3xl font-extrabold text-primary">{selected.title}</h1>
              <div className="flex gap-2 mt-1">
                <Badge className="rounded-full font-bold">{selected.level}</Badge>
                <Badge variant="outline" className="rounded-full uppercase font-semibold"><Globe className="h-3 w-3 mr-1" />{selected.language}</Badge>
              </div>
            </div>
          </div>
          {selected.description && <p className="text-muted-foreground mb-6">{selected.description}</p>}

          <div className="space-y-8">
            {(selected.modules || []).map((m) => (
              <section key={m.id} data-testid={`student-module-${m.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-9 w-9 rounded-xl bg-secondary/15 flex items-center justify-center shrink-0"><Layers className="h-5 w-5 text-secondary" /></span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading text-lg font-bold text-primary truncate">{m.title}</h2>
                    {m.description && <p className="text-xs text-muted-foreground line-clamp-1">{m.description}</p>}
                  </div>
                  <Badge variant="outline" className="rounded-full font-semibold shrink-0">{m.lessons_completed}/{m.lessons_total}</Badge>
                </div>
                <div className="space-y-3 sm:pl-6 sm:border-l-2 border-dashed border-border">
                  {m.lessons.map((l) => <LessonNode key={l.id} lesson={l} onOpen={setOpenLesson} />)}
                  {m.lessons.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
                </div>
              </section>
            ))}
            {legacy.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-bold text-primary mb-3">{t("teacher.no_module")}</h2>
                <div className="space-y-3">{legacy.map((l) => <LessonNode key={l.id} lesson={l} onOpen={setOpenLesson} />)}</div>
              </section>
            )}
            {(selected.modules || []).length === 0 && legacy.length === 0 &&
              <Card className="rounded-2xl p-10 text-center text-muted-foreground">{t("teacher.no_lessons")}</Card>}
          </div>
        </motion.div>

        <LessonDialog openLesson={openLesson} setOpenLesson={setOpenLesson} completeLesson={completeLesson} completing={completing} />
      </AppLayout>
    );
  }

  // ---- Course list + progress ----
  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <Tabs defaultValue="courses" className="space-y-8" onValueChange={(v) => { if (v === "progress") load(); }}>
          <TabsList className="rounded-full bg-muted p-1.5 h-auto">
            <TabsTrigger value="courses" data-testid="tab-courses" className={tabCls}><GraduationCap className="h-4 w-4 mr-2" />{t("student.my_learning")}</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress" className={tabCls}><BarChart3 className="h-4 w-4 mr-2" />{t("student.progress")}</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="space-y-7">
            {overview && (overview.lessons_total > 0) && (
              <Card className="rounded-[1.6rem] p-6 sm:p-8 bg-primary text-primary-foreground border-0 overflow-hidden relative" data-testid="student-overview-card">
                <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-secondary/20" />
                <div className="relative flex flex-wrap items-center gap-6 justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-secondary font-semibold"><Sparkles className="h-4 w-4" />{overview.studied_today ? t("student.studied_today") : t("student.keep_going")}</div>
                    <div className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold">{overview.percent}% <span className="text-base font-semibold text-white/70">{t("student.percent_complete")}</span></div>
                    <div className="mt-3 h-2.5 w-56 max-w-full rounded-full bg-white/15 overflow-hidden">
                      <div className="h-full bg-secondary rounded-full transition-all" style={{ width: `${overview.percent}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Stat value={`${overview.lessons_completed}/${overview.lessons_total}`} label={t("student.lessons_done")} />
                    <Stat value={overview.average_score ?? "—"} label={t("student.avg")} />
                  </div>
                </div>
              </Card>
            )}

            <div>
              <h1 className="font-heading text-4xl font-extrabold tracking-tight text-primary flex items-center gap-3">
                <span className="h-11 w-11 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center"><GraduationCap className="h-6 w-6 text-secondary" /></span>
                {t("student.my_learning")}
              </h1>
              <p className="text-muted-foreground mt-2 text-base">{t("student.my_learning_sub")}</p>
            </div>
            {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-secondary" /></div>
              : courses.length === 0 ? (
                <Card className="rounded-[1.4rem] p-12 text-center text-muted-foreground" data-testid="no-courses-message">{t("student.no_courses")}</Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.map((c) => (
                    <Card key={c.id} data-testid={`student-course-card-${c.id}`}
                      className="rounded-[1.4rem] p-7 cursor-pointer border-border hover:-translate-y-1.5 transition-transform shadow-[0_6px_24px_rgb(20,38,77,0.05)] group overflow-hidden relative"
                      onClick={() => openCourse(c.id)}>
                      <div className="absolute top-0 right-0 h-24 w-24 bg-secondary/10 rounded-full -translate-y-8 translate-x-8" />
                      <div className="flex items-start justify-between mb-5 relative">
                        <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center ring-2 ring-secondary/40 group-hover:scale-105 transition-transform"><BookOpen className="h-7 w-7 text-secondary" /></div>
                        <div className="flex flex-col items-end gap-1.5"><Badge className="rounded-full font-bold">{c.level}</Badge><Badge variant="outline" className="rounded-full uppercase font-semibold"><Globe className="h-3 w-3 mr-1" />{c.language}</Badge></div>
                      </div>
                      <h3 className="font-heading text-xl font-bold relative">{c.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 relative">{c.description}</p>
                      <div className="flex items-center text-secondary text-sm font-bold mt-5 relative">{t("student.start")} <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" /></div>
                    </Card>
                  ))}
                </div>
              )}
          </TabsContent>

          <TabsContent value="progress" className="space-y-6">
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-primary flex items-center gap-3">
              <span className="h-11 w-11 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center"><Award className="h-6 w-6 text-secondary" /></span>
              {t("student.progress")}
            </h1>
            {progress.length === 0 ? <p className="text-muted-foreground">{t("student.no_progress")}</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {progress.map((s) => (
                  <Card key={s.id} data-testid={`student-progress-${s.id}`} className="rounded-[1.4rem] p-6 border-border shadow-[0_6px_24px_rgb(20,38,77,0.05)]">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Badge variant="outline" className="rounded-full font-semibold"><FileText className="h-3.5 w-3.5 mr-1" />{t(`ex.${s.exercise_type}`)}</Badge>
                      {s.grade && <Badge variant="secondary" className="rounded-full font-semibold">{t(`grade.${s.grade}`)}</Badge>}
                    </div>
                    <div className="flex items-end gap-2 mt-4">
                      <span className="font-heading text-4xl font-extrabold text-primary">{s.score ?? "—"}</span>
                      <span className="text-muted-foreground mb-1 text-sm">/100</span>
                    </div>
                    {s.teacher_comment && <p className="text-sm mt-3 rounded-xl bg-muted p-3">📝 {s.teacher_comment}</p>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </AppLayout>
  );
}

function LessonDialog({ openLesson, setOpenLesson, completeLesson, completing }) {
  const { t } = useI18n();
  return (
    <Dialog open={!!openLesson} onOpenChange={(o) => !o && setOpenLesson(null)}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
        {openLesson && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2"><FileText className="h-5 w-5 text-secondary" />{openLesson.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              {openLesson.explanation && (
                <div className="rounded-2xl bg-muted/40 p-4">
                  <div className="text-xs font-bold uppercase text-secondary mb-1">{t("student.explanation")}</div>
                  <p className="text-sm leading-relaxed whitespace-pre-line">{openLesson.explanation}</p>
                </div>
              )}
              {openLesson.content && <p className="text-base leading-relaxed font-medium">{openLesson.content}</p>}
              {(openLesson.media || []).map((m, i) => <MediaEmbed key={i} media={m} />)}

              {(openLesson.vocabulary || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-secondary mb-2">{t("student.vocabulary")}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {openLesson.vocabulary.map((v, i) => (
                      <div key={i} className="rounded-xl border border-border px-3 py-2 flex justify-between gap-2 text-sm">
                        <span className="font-semibold">{v.term}</span>
                        <span className="text-muted-foreground">{v.meaning}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(openLesson.example_phrases || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-secondary mb-2">{t("student.examples")}</div>
                  <div className="flex flex-wrap gap-2">
                    {openLesson.example_phrases.map((p, i) => <Badge key={i} variant="outline" className="rounded-full font-normal">{p}</Badge>)}
                  </div>
                </div>
              )}

              {(openLesson.exercises || []).length > 0 && (
                <div className="space-y-3 pt-1">
                  {openLesson.exercises.map((ex) => <ExercisePlayer key={ex.id} exercise={ex} />)}
                </div>
              )}

              <div className="pt-1">
                {openLesson.state === "completed" ? (
                  <div className="rounded-2xl bg-emerald-50 text-emerald-700 p-4 flex items-center gap-2 font-semibold" data-testid="lesson-completed-banner">
                    <CheckCircle2 className="h-5 w-5" />{t("student.lesson_completed")}
                  </div>
                ) : (
                  <Button onClick={() => completeLesson(openLesson.id)} disabled={completing} className="rounded-full w-full h-12 font-bold" data-testid="complete-lesson-button">
                    {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-2" />{t("student.complete_lesson")}</>}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
